import React, { useContext, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import './LottoSummary.scss';
import LottoContext from '../Context/Lotto.context';
import { RootState } from '../../../configs/store';
import  formatOdds  from '../../../utils/formatOdds';
import Icon from '../../Icon/Icon';
import CombinationsModal from './CombinationsModal/CombinationsModal';
import useIsMobile from 'src/hooks/useIsMobile';
import ToolTip from 'src/components/ToolTip/ToolTip';
import { generateCalculationsPayload } from '../../../utils/lottoHelper';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import { getNumberOfCombinations } from 'src/utils/utils';

export default function LottoSummary()  {
  const {
    selectedEvents,
    selectedMarket,
    selectedNumbers,
    setselectedNumbers,
    combinations,
    setCombinations,
    setUsedNumbers,
    quickBet
  } = useContext(LottoContext);
  const { singleNumberEvent: { numberInfos: { bonus }, tournamentMarkets }, singleNumberEvent } = useSelector((state: RootState) => state.lotto);
  const { slip, betResult } = useSelector((state: RootState) => state.betslip);
  const { authenticated } = useSelector((state: RootState) => state.auth);
  const { stakingRules }  = useSelector((state: RootState) => state.content);
  const activeWallet = useSelector((state: RootState) => state.auth.localWalletSelected);
  const { selectedOddsType, selectedTimezone } = useSelector((state: RootState) => state.odds);

  const events = _.filter(selectedEvents, { selected: true });
  const { RETAIL_MODE } = useContext(AppModeContext);

  const [showWarning, setShowWarning] = useState('');
  const [showCombinations, setShowCombinations] = useState(false);
  const [stake, setStake] = useState((stakingRules?.lotto?.single?.default).toFixed(2).toString() || '2.00');
  const [expanded, setExpanded] = useState(false);
  const [hasOverFlow, sethasOverFlow] = useState(false);
  const [availableMarkets, setAvailableMarkets] = useState(false);

  const { t } = useTranslation();
  const mobile = useIsMobile();
  const dispatch = useDispatch();
  const overFlow = useRef(null);

  let date: string;

  if (events && events.length > 0) {
    date = format(utcToZonedTime(events[0].scheduled* 1000, selectedTimezone), 'EEEE, d LLLL');
    if (events.length > 1) {
      date += ` +${ events.length - 1 } Draws`;
    }
  }

  let market = !selectedMarket || !selectedMarket.specifiers ? '' :
    `'${ selectedMarket?.header }' ${ selectedMarket?.specifiers[0].value } ${ selectedMarket?.specifiers[0].name }
  @ ${ formatOdds(selectedMarket?.outcomes[0].value, selectedOddsType) }:`;

  if (selectedMarket?.typeCode === 800) {
    market = `'${ selectedMarket?.header }' ${ selectedMarket?.specifiers[0].value } + ${ selectedMarket?.specifiers[1].value }
    @ ${ formatOdds(selectedMarket?.outcomes[0].value, selectedOddsType) }:`;
  }

  let filteredBonus;
  const filteredEvents = !bonus?.sameBallset && selectedMarket?.typeCode === 700 ? _.filter(selectedNumbers, { type: 'star' })
    : _.filter(selectedNumbers, { type: 'ball' });


  if (selectedMarket?.typeCode === 800){
    filteredBonus = _.filter(selectedNumbers, { type: 'bonus' });
  }

  const handleCombinations = async (forceCall= false) => {
    if (selectedMarket && selectedEvents.length > 0) {
      let numbers = _.map(filteredEvents, (number) => number.number);
      if (selectedMarket.typeCode === 800 && filteredBonus.length > 0){
        numbers = [...numbers];
      }

      const bankers = _.map(_.filter(filteredEvents, { isBanker:true }), (banker) => banker.number);
      const specifiers = selectedMarket?.specifiers;
      const numbersToPredictCount = selectedMarket?.specifiers && specifiers?.length > 1 ?
        +specifiers[0].value + +specifiers[1].value : +specifiers[0].value;

      const payload = generateCalculationsPayload(
        numbers,
        bankers,
        selectedMarket,
        selectedEvents[0].code,
        +stake,
        numbersToPredictCount,
        filteredBonus
      );

      if (bankers.length > 0 || selectedMarket?.specifiers[1]?.value || forceCall){
        // Here we get permutations
        const response = async () => {
          const data = await dispatch.lotto.permutations(payload);
          setCombinations(data?.bets[0]);
        };
        if (selectedNumbers.length >= +selectedMarket?.specifiers[0].value){
          response();
        } else {
          setCombinations({});
        }
      } else {
        const permutationsCount = getNumberOfCombinations(selectedNumbers, (+selectedMarket?.specifiers[0].value));
        setCombinations({ prediction: { permutationsCount, totalStake: +stake } });
      }
    }
  };

  useEffect(() => {
    if (quickBet) return;
    handleCombinations();
  }, [quickBet, selectedMarket, selectedNumbers, stake]);

  useEffect(() => {
    if (showWarning) {
      setTimeout(
        () => setShowWarning(''),
        3000
      );
    }
  }, [showWarning]);

  useEffect(() => {
    //Check if we show Expand Bar
    if ((mobile && overFlow.current?.clientWidth <= overFlow.current?.lastChild?.offsetLeft) ||
     (!mobile && overFlow.current?.clientWidth >= 223)) {
      sethasOverFlow(true);
    } else {
      sethasOverFlow(false);
    }
  }, [selectedNumbers]);

  const addToBetSlip = async (placeBet=false) => {
    const events = _.filter(selectedEvents, { selected: true });
    const bets = [];
    const specifiers = selectedMarket?.specifiers;
    const numbersToPredictCount = selectedMarket?.specifiers && specifiers?.length > 1 ?
      +specifiers[0].value + +specifiers[1].value : +specifiers[0].value;
    for (const event of events){
      const eventIndex = _.findIndex(tournamentMarkets, { eventCode: event.code });
      const market = _.find(tournamentMarkets[eventIndex]?.markets, {
        marketTypeId: selectedMarket.marketTypeId,
        name: selectedMarket.name });
      if (market){
        bets.push({
          eventCode: event.code,
          eventName: singleNumberEvent.name,
          providerCode: 1,
          odds: market.outcomes[0].value,
          marketCode: market.code,
          marketOutcomeCode: market.outcomes[0].code,
          marketName: market.name,
          tournamentName: singleNumberEvent.name,
          datetime: event.scheduled * 1000,
          sport: 'lotto',
          type: 'numbers',
          country: singleNumberEvent.country,
          selectedNumbers: selectedNumbers,
          jsonLabel: selectedNumbers,
          stake: stake,
          permutationsCount: combinations?.prediction?.permutationsCount || 1,
          productType: 4,
          numbersToPredictCount,
          repetitionsCount: events.length,
          sameBallSet: bonus?.sameBallset,
          market: selectedMarket
        });
      } else {
        setAvailableMarkets(true);
      }
    }
    if (betResult){
      await dispatch.betslip.clearBetResult();
    }

    if (slip === 0){
      await dispatch.betslip.changeStakeSelection('differentStake');
    }
    if (!placeBet){
      await dispatch.betslip.addBets(bets);
    } else {
      await dispatch.betslip.placeBet({ isRetail: RETAIL_MODE, bet:bets });
    }
    setUsedNumbers(selectedNumbers);
    setselectedNumbers([]);
  };

  const checkIfButttonsDisabled = () => {
    if (selectedMarket && filteredEvents){
      if (selectedMarket && selectedMarket.typeCode === 800){
        return filteredEvents?.length < +selectedMarket?.specifiers[0].value || filteredBonus?.length  < +selectedMarket?.specifiers[1].value;
      }
      return filteredEvents?.length < +selectedMarket?.specifiers[0].value;
    }
    return true;
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    let _slip = 'single';
    if (slip === 1){
      _slip = 'acca';
    } else if (slip === 2){
      _slip = 'system';
    }

    const { name } = e.target;
    const isComboBet = slip!==0 && combinations?.prediction?.permutationsCount > 1;
    if (checkIfButttonsDisabled() || (isComboBet && name==='addBet')){
      let message =  `${ t('numbers.pickAtLeast') } ${ +selectedMarket?.specifiers[0].value } ${ +selectedMarket?.specifiers[0].value > 1 ?
        t('numbers.numberPlural') : t('numbers.numberSingular') }`;

      if (slip !==0 && combinations.prediction.permutationsCount > 1 && name==='addBet'){
        message = t('numbers.addToBetslipError');
      }

      setShowWarning(message);
    } else if (name === 'quickBet' && +stake < stakingRules?.lotto[_slip]?.minBetslip) {

      const message = `${ t('betslip.stakeMinimumError') } ${ stakingRules.lotto[_slip].minBetslip }`;
      setShowWarning(message);
    } else if (name=== 'quickBet' || !name){
      if (authenticated || RETAIL_MODE){
        await addToBetSlip(true);
      } else {
        dispatch.content.modalType('#login-no-redirect');
      }
    } else {
      await addToBetSlip();
    }
  };

  const handleClose = (e) => {
    e.preventDefault();
    setShowWarning('');
  };

  const handleExpand = (e) => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  const convertToFixed = () => {
    const newStake = (+stake).toFixed(2);
    setStake(newStake.toString());
  };

  const handleCombinationsClick = async (e) => {
    e.preventDefault();
    await handleCombinations(true);
    setShowCombinations(true);
  };

  const checkValue = () => {
    if (combinations && combinations.prediction?.totalStake > 0 && combinations.prediction?.permutationsCount >= 1){

      if (combinations.prediction['totalStake'] && combinations.prediction['stake']) {
        return combinations.prediction['totalStake'];
      }
      return Math.abs(combinations.prediction.totalStake * -(+(combinations.prediction.permutationsCount)));
    }

    return '-.--';
  };

  return (
    <>
      {
        availableMarkets &&  <ToolTip
          title= { t('numbers.someMarketsNotAvailable') }
          message={ t('numbers.addAvailableToBetslip') }
          className="number-picker__tooltip--hidden"
          isOpen
          handleClick={ () => setAvailableMarkets(!availableMarkets) }
        />
      }
      {
        !showWarning && (
          <>
            {
              (hasOverFlow || expanded) &&
              (
                <div className="lotto-summary__expand" onClick={ handleExpand } />
              )
            }
            <div className="lotto-summary__info">
              <div>{ `${ singleNumberEvent.name } - ${ date }` }</div>
              { market }
              <div className="lotto-summary__wrapper">

                <div
                  className={
                    classNames({
                      'lotto-summary__numbers': true,
                      'lotto-summary__numbers--expanded': expanded,
                      'lotto-summary__numbers--full':selectedMarket?.typeCode === 800
                    })
                  }
                  ref={ overFlow }
                >
                  {
                    selectedMarket && selectedMarket.typeCode === 800 && filteredBonus && (
                      <span className="lotto-summary__title">Main</span>
                    )
                  }
                  {
                    selectedNumbers.length > 0 &&
                    _.map(filteredEvents, (ball, index) => {
                      return (
                        <span
                          key={ index }
                          className={
                            classNames({
                              'lotto-summary__number': true,
                              'lotto-summary__number--banker': ball.isBanker
                            })
                          }
                        >{ ball.number }{ index < filteredEvents.length -1 && (', ') }</span>
                      );
                    })
                  }
                </div>


                {
                  selectedNumbers.length >0 && selectedMarket.typeCode === 800 && (<div
                    className={
                      classNames({
                        'lotto-summary__numbers': true,
                        'lotto-summary__numbers--expanded': expanded,
                        'lotto-summary__numbers--full':selectedMarket.typeCode === 800
                      })
                    }
                    ref={ overFlow }
                  >
                    {
                      selectedMarket.typeCode === 800 && filteredBonus && (
                        <span className="lotto-summary__title">Bonus</span>
                      )
                    }
                    {
                      _.map(filteredBonus, (ball, index) => {
                        return (
                          <span
                            key={ index }
                            className={
                              classNames({
                                'lotto-summary__number': true,
                                'lotto-summary__number--banker': ball.isBanker
                              })
                            }
                          >{ ball.number }{ +index < filteredBonus.length -1 && (', ') }</span>
                        );
                      })
                    }
                  </div>)
                }
              </div>
              {
                combinations?.prediction?.permutationsCount > 0 && <div
                  className="lotto-summary__combinations"
                  onClick={ handleCombinationsClick }
                >
                  { t('numbers.combinations') } { combinations.prediction.permutationsCount }
                </div>
              }
            </div>

            <div className ={
              classNames({
                'lotto-summary__button': true,
                'lotto-summary__button--no-padding':!mobile,
                'lotto-summary__button--margin': hasOverFlow && !mobile
              })
            }
            >
              <div className="lotto-summary__stake-container">
                <span className="lotto-summary__stake-label">{ t('numbers.stakePerBet') }</span>
                <input
                  type="text"
                  autoComplete="new-password"
                  value={ stake }
                  onChange={ (e) => setStake(e.target.value) }
                  onFocus={ (e) => e.target.select() }
                  onBlur={ convertToFixed }
                  className="lotto-summary__stake"
                />
              </div>
              <div className="lotto-summary__buttons-container">
                <button
                  type="button"
                  name="quickBet"
                  className={
                    classNames({
                      'lotto-summary__quick-bet' : true,
                      'lotto-summary__quick-bet--disabled': checkIfButttonsDisabled(),
                      'btn btn--cta': true
                    })
                  }
                  onClick={ handleSubmitClick }
                >
                  {
                    (!activeWallet || activeWallet.typeCode !== 81 || _.isEmpty(activeWallet)) &&
                    `${ t('numbers.quickBet') } ${ checkValue() }`
                  }
                  {
                    (activeWallet && activeWallet.typeCode === 81) &&
                      <div className="lotto-summary__bonus-container">
                        <span>Quick</span>
                        <div className="lotto-summary__bonus-badge">Bonus Bet</div>
                        <span>{ checkValue() }</span>
                      </div>
                  }
                </button>
                <button
                  type="button"
                  name="addBet"
                  className={
                    classNames({
                      'lotto-summary__submit' : true,
                      'lotto-summary__submit--disabled': checkIfButttonsDisabled() ||
                    (combinations?.prediction?.permutationsCount > 1 && slip !== 0)
                    })
                  }
                  onClick={ handleSubmitClick }
                >
                  { t('numbers.addToBetslip') }
                </button>
              </div>
            </div>
          </>)
      }
      {
        showWarning && (
          <>
            <div className="lotto-summary__message">
              {
                showWarning
              }
            </div>
            <Icon icon="close" className="lotto-summary__close" onClick={ handleClose } />
          </>
        )
      }

      {
        showCombinations && (
          <>
            <CombinationsModal
              title={ t('numbers.combinations') }
              permutations={ combinations.permutations }
              stake={ combinations.singleStake }
              permutations2={ combinations.prediction?.permutations2 }
              profit={ combinations.prediction.minimumReturn }
              odds={ combinations.odds }
              onClick={ () => setShowCombinations(!showCombinations) }
            />
          </>
        )
      }
    </>
  );
}
